import type { DxfMappingRole } from '@frontend/api';
import {
  type ReadableStore,
  type Selection,
  type SelectionRef,
  deriveStore,
  loopsOnLayer,
  selectionCoversRef,
  selectionOf,
  selectionPositions,
} from '@frontend/viewer-core';

import {
  HIGHLIGHT_ACCENT_WIDTH_PX,
  HOVER_ACCENT_COLOR,
  HOVER_ACCENT_WIDTH_PX,
  PREVIEW_ACCENT_COLOR,
  PREVIEW_ACCENT_WIDTH_PX,
  SELECTION_ACCENT_COLOR,
  SELECTION_ACCENT_WIDTH_PX,
} from '../constants';
import { entityKeyPositions } from './mappingModel';
import type {
  AccentBatch,
  AccentSpec,
  DrawingModel,
  DxfSessionState,
  EntityHighlights,
  MarqueePreview,
  RoleIndex,
} from './types';

/**
 * Fires when anything the accent overlay is drawn from changes, and not
 * otherwise — so panning, resizing and status changes never trigger a rebuild.
 *
 * Lives here rather than in the composition root because it exists purely to
 * tell `attachRenderer` when to call `buildAccents` below — it is this
 * function's own trigger, not a general-purpose session slice.
 */
export const createAccentSourceSlice = (
  state: ReadableStore<DxfSessionState>,
): ReadableStore<unknown> =>
  deriveStore(
    state,
    (current) =>
      [
        current.selection,
        current.hovered,
        current.hoveredLayer,
        current.hoveredRole,
        current.hiddenLayers,
        current.highlights,
        // The drawn hidden layers follow the mode, so it is part of the source.
        current.mode,
      ] as const,
    (a, b) => a.every((value, index) => Object.is(value, b[index])),
  );

/** The fields `buildAccents` actually reads — not the whole session state. */
export type AccentInputs = {
  selection: Selection;
  /** What clicking right now would pick — one ref, several, or none. */
  hovered: readonly SelectionRef[];
  hoveredLayer: number | null;
  /** Role hovered in the mapping panel, or `null`. */
  hoveredRole: DxfMappingRole | null;
  /** Entity keys per role, for resolving `hoveredRole` to geometry. */
  roleIndex: RoleIndex;
  /** The hidden layers as drawn — see `DxfViewerSession.drawnHiddenLayers`. */
  hiddenLayers: ReadonlySet<number>;
  /** Highlighted entities and their colours. */
  highlights: EntityHighlights;
};

/**
 * The geometry a live marquee is about to change.
 *
 * Only what the release would actually *alter* is shown.
 *
 * Under `replace` that is the items not selected yet — re-highlighting ones
 * already caught says nothing, and the items it would drop are simply the rest
 * of the selection, which lighting up would fight the accent already on them for
 * no gain.
 *
 * Under `toggle` it is everything the box caught, selected or not, because every
 * one of them changes state on release. Selected items therefore carry both
 * accents at once, which reads as "these are in play" — the answer with
 * only one preview batch. Distinguishing "about to be added" from "about to be
 * removed" would need a second batch and a second colour, which nothing here
 * needs: no gesture produces a subtract-only preview.
 */
const buildPreviewPositions = (
  model: DrawingModel,
  inputs: AccentInputs,
  preview: MarqueePreview | null,
): { positions: number[]; color: string } => {
  if (!preview || preview.refs.length === 0) {
    return { positions: [], color: PREVIEW_ACCENT_COLOR };
  }

  const refs = preview.refs.filter(
    (ref) =>
      preview.mode === 'toggle' ||
      !selectionCoversRef(inputs.selection, ref, model.loops),
  );

  return {
    positions: selectionPositions(
      selectionOf(refs),
      model.loops,
      inputs.hiddenLayers,
    ),
    color: PREVIEW_ACCENT_COLOR,
  };
};

/** One batch per highlight colour, so each colour is one draw call. */
const buildHighlightBatches = (
  model: DrawingModel,
  highlights: EntityHighlights,
  hiddenLayers: ReadonlySet<number>,
): AccentBatch[] => {
  const keysByColor = new Map<string, string[]>();
  highlights.forEach((color, key) => {
    const keys = keysByColor.get(color);
    if (keys) {
      keys.push(key);
    } else {
      keysByColor.set(color, [key]);
    }
  });
  const batches: AccentBatch[] = [];
  keysByColor.forEach((keys, color) => {
    const positions = entityKeyPositions(keys, model, hiddenLayers);
    if (positions.length > 0) {
      batches.push({ positions, color, widthPx: HIGHLIGHT_ACCENT_WIDTH_PX });
    }
  });
  return batches;
};

/**
 * The current selection, hover and marquee preview, flattened into the
 * geometry the renderer paints.
 *
 * A pure function of its inputs rather than a closure over stores: this is
 * the exact computation that has to run every time the model, the
 * selection/hover, or the marquee preview changes, and giving it plain data
 * in and plain data out is what makes it callable from one place
 * (`attachRenderer`'s push loop) without also handing it access to stores it
 * has no other reason to touch.
 */
export const buildAccents = (
  model: DrawingModel | null,
  inputs: AccentInputs,
  preview: MarqueePreview | null,
): AccentSpec | null => {
  if (!model) {
    return null;
  }
  const { hiddenLayers, hovered, hoveredLayer, hoveredRole, roleIndex } =
    inputs;

  /**
   * What the pointer is promising, from whichever source is offering one.
   *
   * A role hovered in the mapping panel gets the same halo a hovered layer does —
   * the spec asks both panels to preview what a click would select, and reusing the
   * hover accent means they look the same and cost the same. Resolved through entity
   * keys rather than loops because that is how a role is addressed.
   */
  const hoverPositions =
    hoveredRole !== null
      ? entityKeyPositions(
          roleIndex.get(hoveredRole) ?? [],
          model,
          hiddenLayers,
        )
      : selectionPositions(
          selectionOf(
            hoveredLayer !== null
              ? loopsOnLayer(model.loops, hoveredLayer)
              : hovered,
          ),
          model.loops,
          hiddenLayers,
        );
  const selectionPoints = selectionPositions(
    inputs.selection,
    model.loops,
    hiddenLayers,
  );

  const previewBatch = buildPreviewPositions(model, inputs, preview);
  const highlightBatches = buildHighlightBatches(
    model,
    inputs.highlights,
    hiddenLayers,
  );

  if (
    highlightBatches.length === 0 &&
    hoverPositions.length === 0 &&
    selectionPoints.length === 0 &&
    previewBatch.positions.length === 0
  ) {
    return null;
  }

  return {
    highlights: highlightBatches,
    hover: hoverPositions.length
      ? {
          positions: hoverPositions,
          color: HOVER_ACCENT_COLOR,
          widthPx: HOVER_ACCENT_WIDTH_PX,
        }
      : null,
    selection: selectionPoints.length
      ? {
          positions: selectionPoints,
          color: SELECTION_ACCENT_COLOR,
          widthPx: SELECTION_ACCENT_WIDTH_PX,
        }
      : null,
    preview: previewBatch.positions.length
      ? {
          positions: previewBatch.positions,
          color: previewBatch.color,
          widthPx: PREVIEW_ACCENT_WIDTH_PX,
        }
      : null,
  };
};
